/**
 * Modifier keys for keyboard shortcuts
 */
export enum ModifierKey {
    CTRL = 'ctrl',
    CMD = 'meta', // Command key on Mac
    SHIFT = 'shift',
    ALT = 'alt'
}

/**
 * Priority levels for keyboard shortcuts
 * Higher priority shortcuts are processed first
 */
export enum ShortcutPriority {
    LOW = 0,
    NORMAL = 1,
    HIGH = 2,
    CRITICAL = 3
}

/**
 * Configuration for a keyboard shortcut
 */
export interface KeyboardShortcutConfig {
    /** Unique identifier for the shortcut */
    id: string;

    /** The main key(s) to trigger the shortcut (e.g., 'k', 'Enter', 'Escape') */
    keys: string | string[];

    /** Modifier keys that must be pressed (e.g., [ModifierKey.CTRL, ModifierKey.SHIFT]) */
    modifiers?: ModifierKey[];

    /** Callback function to execute when shortcut is triggered */
    handler: (event: KeyboardEvent) => void | boolean;

    /** Optional description for documentation/debugging */
    description?: string;

    /** Context/scope identifier - shortcuts are scoped to prevent conflicts */
    context?: string;

    /** Priority level - higher priority shortcuts are processed first */
    priority?: ShortcutPriority;

    /** Whether to prevent default browser behavior */
    preventDefault?: boolean;

    /** Whether to stop propagation of the event */
    stopPropagation?: boolean;

    /** Whether the shortcut is currently enabled */
    enabled?: boolean;

    /** Element to listen for shortcuts on (defaults to document) */
    target?: HTMLElement;
}

/**
 * Internal registry entry for shortcuts
 */
export interface ShortcutRegistryEntry extends KeyboardShortcutConfig {
    /** Normalized key combination string */
    keyString: string;

    /** Unique registration ID */
    registrationId: string;

    /** Timestamp of registration */
    registeredAt: number;
}
